import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ref, listAll, uploadString, deleteObject } from 'firebase/storage';
import { kbStorage } from '../../firebase';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from '@mui/material';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import DirectoryTreeNode from './DirectoryTreeNode';

/**
 * Sanitize a user-supplied file/folder name.
 * Returns null if the name is invalid.
 */
function sanitizeName(raw) {
  const name = raw.trim();
  if (!name) return null;
  // Reject path separators and dot-dot traversal sequences
  if (name.includes('/') || name.includes('\\') || /(^|\/)\.\.($|\/)/.test(name)) return null;
  return name;
}

/**
 * Flatten a tree into a set of all file paths (for duplicate detection).
 */
function collectFilePaths(nodes, out = new Set()) {
  for (const node of nodes) {
    if (node.type === 'file') {
      out.add(node.path);
    } else {
      collectFilePaths(node.children, out);
    }
  }
  return out;
}

/**
 * Recursively build a tree from a Firebase Storage reference.
 * Returns an array of TreeNode:
 *   { name, path, type: 'folder'|'file', children: TreeNode[] }
 * Filters out .keep placeholder files from the visible tree.
 */
async function buildTree(storageRef) {
  const { items, prefixes } = await listAll(storageRef);

  const fileNodes = items
    .filter((item) => item.name !== '.keep')
    .map((item) => ({ name: item.name, path: item.fullPath, type: 'file', children: [] }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const folderNodes = await Promise.all(
    prefixes.map(async (prefix) => ({
      name: prefix.name,
      path: prefix.fullPath,
      type: 'folder',
      children: await buildTree(prefix),
    }))
  );
  folderNodes.sort((a, b) => a.name.localeCompare(b.name));

  return [...folderNodes, ...fileNodes];
}

/**
 * Recursively delete all objects under a folder path (including .keep files).
 */
async function deleteFolderRecursive(path) {
  const { items, prefixes } = await listAll(ref(kbStorage, path));
  await Promise.all([
    ...items.map((item) => deleteObject(item)),
    ...prefixes.map((p) => deleteFolderRecursive(p.fullPath)),
  ]);
}

/**
 * DirectoryPane — collapsible left-side file-tree panel for the Knowledge Base screen.
 *
 * Props:
 *   currentPath   — currently displayed document path (for active-file highlight)
 *   onFileSelect  — (path) => void — called when user clicks a file
 *   onFileDeleted — (path) => void — called after a file is successfully deleted
 *   canEdit       — boolean — show create/delete controls
 */
export default function DirectoryPane({ currentPath, onFileSelect, onFileDeleted, canEdit }) {
  const [treeData, setTreeData] = useState([]);
  const [treeLoading, setTreeLoading] = useState(true);
  const [treeError, setTreeError] = useState(null);
  const [expanded, setExpanded] = useState(new Set());

  // Dialog state: { type: 'createFile'|'createFolder'|'deleteFile'|'deleteFolder', node, parentPath }
  const [dialog, setDialog] = useState(null);
  const [newName, setNewName] = useState('');
  const [nameError, setNameError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(null);

  // ── Tree loading ────────────────────────────────────────────────────────────

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const loadTree = useCallback(async () => {
    setTreeLoading(true);
    setTreeError(null);
    try {
      const data = await buildTree(ref(kbStorage, '/'));
      if (!mountedRef.current) return;
      setTreeData(data);
    } catch {
      if (!mountedRef.current) return;
      setTreeError('Could not load directory. Please try again.');
    } finally {
      if (mountedRef.current) setTreeLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTree();
  }, [loadTree]);

  // ── Tree interaction ────────────────────────────────────────────────────────

  const handleToggle = useCallback((path) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  }, []);

  // ── Dialog helpers ──────────────────────────────────────────────────────────

  const openCreateFile = useCallback((parentPath) => {
    setDialog({ type: 'createFile', parentPath });
    setNewName('');
    setNameError('');
    setActionError(null);
  }, []);

  const openCreateFolder = useCallback((parentPath) => {
    setDialog({ type: 'createFolder', parentPath });
    setNewName('');
    setNameError('');
    setActionError(null);
  }, []);

  const openDelete = useCallback((node) => {
    setDialog({ type: node.type === 'folder' ? 'deleteFolder' : 'deleteFile', node });
    setActionError(null);
  }, []);

  const closeDialog = useCallback(() => {
    if (actionLoading) return;
    setDialog(null);
    setNewName('');
    setNameError('');
    setActionError(null);
  }, [actionLoading]);

  // ── CRUD operations ─────────────────────────────────────────────────────────

  const handleCreateFile = useCallback(async () => {
    const name = sanitizeName(newName);
    if (!name) {
      setNameError('Invalid filename.');
      return;
    }
    const finalName = name.endsWith('.md') ? name : `${name}.md`;
    const filePath = dialog.parentPath ? `${dialog.parentPath}/${finalName}` : finalName;

    // Duplicate check against current tree snapshot
    const existingPaths = collectFilePaths(treeData);
    if (existingPaths.has(filePath)) {
      setNameError('A file with this name already exists.');
      return;
    }

    setActionLoading(true);
    setActionError(null);
    try {
      await uploadString(ref(kbStorage, filePath), '# New Document\n', 'raw', {
        contentType: 'text/plain; charset=utf-8',
      });
      await loadTree();
      // Auto-expand the parent folder so the new file is visible
      if (dialog.parentPath) {
        setExpanded((prev) => new Set([...prev, dialog.parentPath]));
      }
      setDialog(null);
      setNewName('');
    } catch {
      setActionError('Failed to create file. Please try again.');
    } finally {
      setActionLoading(false);
    }
  }, [newName, dialog, treeData, loadTree]);

  const handleCreateFolder = useCallback(async () => {
    const name = sanitizeName(newName);
    if (!name) {
      setNameError('Invalid folder name.');
      return;
    }
    const folderPath = dialog.parentPath ? `${dialog.parentPath}/${name}` : name;
    const keepPath = `${folderPath}/.keep`;

    setActionLoading(true);
    setActionError(null);
    try {
      await uploadString(ref(kbStorage, keepPath), '', 'raw', {
        contentType: 'text/plain',
      });
      await loadTree();
      setExpanded((prev) => new Set([...prev, folderPath]));
      if (dialog.parentPath) {
        setExpanded((prev) => new Set([...prev, dialog.parentPath]));
      }
      setDialog(null);
      setNewName('');
    } catch {
      setActionError('Failed to create folder. Please try again.');
    } finally {
      setActionLoading(false);
    }
  }, [newName, dialog, loadTree]);

  const handleDeleteFile = useCallback(async () => {
    const deletedPath = dialog.node.path;
    setActionLoading(true);
    setActionError(null);
    try {
      await deleteObject(ref(kbStorage, deletedPath));
      await loadTree();
      setDialog(null);
      onFileDeleted?.(deletedPath);
    } catch {
      setActionError('Failed to delete file. Please try again.');
    } finally {
      setActionLoading(false);
    }
  }, [dialog, loadTree, onFileDeleted]);

  const handleDeleteFolder = useCallback(async () => {
    setActionLoading(true);
    setActionError(null);
    try {
      await deleteFolderRecursive(dialog.node.path);
      await loadTree();
      setDialog(null);
    } catch {
      setActionError('Failed to delete folder. Please try again.');
    } finally {
      setActionLoading(false);
    }
  }, [dialog, loadTree]);

  // ── Render helpers ──────────────────────────────────────────────────────────

  const folderNonEmpty = dialog?.type === 'deleteFolder' && dialog.node?.children?.length > 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Pane header */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.75, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        <Typography variant="caption" sx={{ flex: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', fontSize: '0.7rem' }}>
          Files
        </Typography>
        {canEdit && (
          <Box sx={{ display: 'flex' }}>
            <Tooltip title="New file at root">
              <IconButton size="small" sx={{ p: 0.5 }} onClick={() => openCreateFile('')} disabled={treeLoading}>
                <NoteAddIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="New folder at root">
              <IconButton size="small" sx={{ p: 0.5 }} onClick={() => openCreateFolder('')} disabled={treeLoading}>
                <CreateNewFolderIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Tree content */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', py: 0.5 }}>
        {treeLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 3 }}>
            <CircularProgress size={20} />
          </Box>
        )}
        {!treeLoading && treeError && (
          <Alert severity="error" sx={{ m: 1, fontSize: '0.75rem' }}>
            {treeError}
          </Alert>
        )}
        {!treeLoading && !treeError && treeData.length === 0 && (
          <Typography variant="caption" sx={{ px: 2, color: 'text.disabled', display: 'block', pt: 1 }}>
            No files found.
          </Typography>
        )}
        {!treeLoading && !treeError && treeData.map((node) => (
          <DirectoryTreeNode
            key={node.path}
            node={node}
            depth={0}
            expanded={expanded}
            onToggle={handleToggle}
            currentPath={currentPath}
            onFileSelect={onFileSelect}
            canEdit={canEdit}
            onCreateFile={openCreateFile}
            onCreateFolder={openCreateFolder}
            onDelete={openDelete}
          />
        ))}
      </Box>

      {/* ── Dialogs ── */}

      {/* Create file */}
      <Dialog open={dialog?.type === 'createFile'} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>New File</DialogTitle>
        <DialogContent>
          {actionError && <Alert severity="error" sx={{ mb: 2 }}>{actionError}</Alert>}
          <TextField
            autoFocus
            fullWidth
            label="Filename (.md)"
            value={newName}
            onChange={(e) => { setNewName(e.target.value); setNameError(''); }}
            error={!!nameError}
            helperText={nameError || (dialog?.parentPath ? `Will be created in: ${dialog.parentPath}/` : 'Will be created at root')}
            onKeyDown={(e) => { if (e.key === 'Enter') handleCreateFile(); }}
            disabled={actionLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={actionLoading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateFile}
            disabled={actionLoading || !newName.trim()}
            startIcon={actionLoading ? <CircularProgress size={14} color="inherit" /> : null}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create folder */}
      <Dialog open={dialog?.type === 'createFolder'} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>New Folder</DialogTitle>
        <DialogContent>
          {actionError && <Alert severity="error" sx={{ mb: 2 }}>{actionError}</Alert>}
          <TextField
            autoFocus
            fullWidth
            label="Folder name"
            value={newName}
            onChange={(e) => { setNewName(e.target.value); setNameError(''); }}
            error={!!nameError}
            helperText={nameError || (dialog?.parentPath ? `Will be created in: ${dialog.parentPath}/` : 'Will be created at root')}
            onKeyDown={(e) => { if (e.key === 'Enter') handleCreateFolder(); }}
            disabled={actionLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={actionLoading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateFolder}
            disabled={actionLoading || !newName.trim()}
            startIcon={actionLoading ? <CircularProgress size={14} color="inherit" /> : null}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete file */}
      <Dialog open={dialog?.type === 'deleteFile'} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Delete File</DialogTitle>
        <DialogContent>
          {actionError && <Alert severity="error" sx={{ mb: 2 }}>{actionError}</Alert>}
          <Typography>
            Delete <strong>{dialog?.node?.name}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={actionLoading}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteFile}
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={14} color="inherit" /> : null}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete folder */}
      <Dialog open={dialog?.type === 'deleteFolder'} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Folder</DialogTitle>
        <DialogContent>
          {actionError && <Alert severity="error" sx={{ mb: 2 }}>{actionError}</Alert>}
          <Typography>
            Delete folder <strong>{dialog?.node?.name}</strong>?
          </Typography>
          {folderNonEmpty && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              This folder is not empty. All files and subfolders inside will be permanently deleted.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={actionLoading}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteFolder}
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {folderNonEmpty ? 'Delete All' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
